import { actions, elements, feedback, inputs } from '@ecosystem/newron-design-system';
import { GenericCell } from '@components/design/migrated/Table';
import classnames from 'classnames';
import { noop } from 'lodash';
import React, { useState } from 'react';
import { ColumnHeader, Feature, GroupState, ItemFeaturesResp, LeaderboardKpi, SaleType } from '@models/Products';
import { isMarketRank, isRetailerRank } from '@utils/hook/tableCellConst/TableCellConst';
import { GrowthTypes } from '@components/comparison-growth-selector/Types';
import MarketShareCell from '@components/products-top-5-table/market-share-cell/MarketShareCell';
import { FocusArea } from '@components/products-top-5-table/Types';
import { getColorByIndex } from '@components/products-top-5-table/utils/ProductsUtils';
import ProductsFeatures from '../products-features/ProductsFeatures';
import ProductsTableDataCell from '../products-table-data-cell/ProductsTableDataCell';
import { DisplayOptions } from '../products-table-filter/Types';
import ProductsTableFirstActivityCell from '../products-table-first-activity-cell/ProductsTableFirstActivityCell';
import ProductsTableRankCell from '../products-table-rank-cell/ProductsTableRankCell';
import * as styles from './ProductTableCell.module.scss';
import { useProductAnalyzerGroupEventState } from '../product-table-grouping/utils/ProductAnalyzerGroupContext';
import { isPeriodYTDOrMAT } from '@utils/PeriodUtils';
import { useSelector } from 'react-redux';
import { IRootState } from '@state/reducers/Reducers';
import { isShareGroupButtonDisable } from '@utils/TableUtils';
import { FEATURE_UNAVAILABLE } from '@components/products-compare-modal/Types';

interface Props {
  selectedDisplayType: DisplayOptions;
  headerKey: string;
  leaderboardKpi: LeaderboardKpi;
  itemFeature: ItemFeaturesResp;
  isStickyRow?: boolean;
  isItemModel: boolean;
  sortedColumn?: string;
  currency: string;
  selectedGrowthType: GrowthTypes;
  selectedSaleType?: SaleType;
  selectedFocusArea?: FocusArea;
  rowIndex?: number;
  isChecked?: boolean;
  groupState?: GroupState;
  isRowHovered?: boolean;
}

const ProductsTableCell: React.FC<Props> = ({
  selectedDisplayType,
  headerKey,
  leaderboardKpi,
  itemFeature,
  isStickyRow,
  sortedColumn,
  isItemModel,
  currency,
  selectedGrowthType,
  selectedSaleType,
  selectedFocusArea = FocusArea.market,
  rowIndex,
  isChecked = false,
  groupState,
  isRowHovered,
}) => {
  const { period } = useSelector((state: IRootState) => state.mainFiltersProducts);

  const [toggleGroupDown, setToggleGroupDown] = useState<boolean>(false);
  const {
    isEditGroupMode,
    isModelGroupSelected,
    setIsDeleteModalGroupPopUp,
    setGroupNameToBeDelete,
    setIsShareModalGroupPopUp,
  } = useProductAnalyzerGroupEventState();

  const displayRanking = (key: any) => {
    if (isMarketRank(key)) {
      return leaderboardKpi.marketRank;
    } else if (isRetailerRank(key)) {
      return leaderboardKpi.retailerRank;
    }
  };

  const { Menu, IconButton } = actions;
  const { ChevronUp, ChevronDown, AddGroup, Share, Delete } = IconButton.icons;
  const { Icon } = elements;
  const getColor = () => {
    let colorIndex = 0;

    if (selectedFocusArea === FocusArea.market && leaderboardKpi.marketRank) {
      colorIndex = leaderboardKpi.marketRank.rank - 1;
    }

    if (selectedFocusArea === FocusArea.retailer && leaderboardKpi.retailerRank) {
      colorIndex = leaderboardKpi.retailerRank.rank - 1;
    }

    return getColorByIndex(colorIndex);
  };

  const getColorLegend = () => {
    if (selectedDisplayType !== DisplayOptions.overview) {
      return <></>;
    }

    return (
      <span
        className={styles.ProductTableCell__TableCell_Item_Legend}
        style={{ backgroundColor: styles[getColor()] }}
      />
    );
  };

  //Check if checkbox should be checked
  const isCheckboxCheck = (): inputs.CheckboxState => {
    if (isEditGroupMode.isEditMode && isEditGroupMode.groupDetails.itemGroup.includes(leaderboardKpi.item.itemId)) {
      return inputs.CheckboxState.CHECKED;
    }

    return isModelGroupSelected(leaderboardKpi.item.itemId)
      ? inputs.CheckboxState.CHECKED
      : inputs.CheckboxState.UNCHECKED;
  };

  //Check if checkbox should be disabled
  const isCheckboxDisable = (): boolean => {
    return isEditGroupMode.isEditMode && isEditGroupMode.groupDetails.itemGroup.includes(leaderboardKpi.item.itemId);
  };
  if (ColumnHeader.checkbox === headerKey) {
    const checked = isChecked ? inputs.CheckboxState.CHECKED : inputs.CheckboxState.UNCHECKED;

    const getState = () => {
      return isEditGroupMode.isEditMode ? isCheckboxCheck() : checked;
    };
    const showCheckbox = !groupState && leaderboardKpi.item.itemId && leaderboardKpi.item.itemName !== 'TOTAL';
    return (
      <td className={styles.ProductTableCell__TableCell}>
        {showCheckbox && (
          <inputs.Checkbox
            testId="product-table-checkbox"
            disabled={isCheckboxDisable()}
            state={getState()}
            onChange={noop}
            onClick={noop}
          ></inputs.Checkbox>
        )}
      </td>
    );
  }

  //Grouped Item Cell
  //Handle Group dropdown state
  const handleGroupDropdownToggle = () => {
    const prevState = toggleGroupDown;

    setToggleGroupDown(!prevState);
    groupState?.setIsGroupingRowExpanded?.(!prevState);
  };

  //Handle to open of the delete modal group state
  const openDeleteModalGroup = () => {
    setGroupNameToBeDelete(groupState?.groupName);
    setIsDeleteModalGroupPopUp(true);
  };

  const openShareModalGroup = () => {
    setGroupNameToBeDelete(groupState?.groupName);
    setIsShareModalGroupPopUp(true);
  };

  const isGroupingCell = groupState && isItemModel;

  const returnGroupingCell = (isHeader: boolean) => {
    if (isHeader) {
      return (
        <div
          className={classnames([
            styles.ProductTableCell__TableCell_Item,
            styles.ProductTableCell__TableCell__grouping,
          ])}
        >
          <IconButton
            onClick={handleGroupDropdownToggle}
            disabled={groupState?.isGroupEmpty || isEditGroupMode.isEditMode} //NOSONAR typescript:S6606
          >
            {toggleGroupDown && !isEditGroupMode.isEditMode ? <ChevronUp /> : <ChevronDown />}
          </IconButton>
          <span className={styles.ProductTableCell__TableCell_Icons}>{getColorLegend()}</span>
          <GenericCell
            alignment="left"
            data-testid="generic-cell-item-name"
            primary={<span className={styles.GenericCell__Title}>{leaderboardKpi.item.itemBrand}</span>}
            className={styles.GenericCell}
          />
          <div className={styles.ProductTableCell__TableCell_Icons_Padding_Header}>
            {selectedDisplayType === DisplayOptions.data && (
              <ProductsFeatures feature={itemFeature.features} firstActivity={leaderboardKpi.item.firstActivityDate} />
            )}
            {isRowHovered ? (
              <Menu isDisabled={isEditGroupMode.isEditMode}>
                <Menu.MoreButton />
                <Menu.List>
                  {groupState?.isPreferenceOwner ? (
                    <>
                      <Menu.Item onSelect={groupState?.enableIsEditGroupMode!}>
                        <Icon>
                          <AddGroup />
                        </Icon>
                        {`Add to group`}
                      </Menu.Item>
                      <Menu.Item onSelect={openShareModalGroup} isDisabled={isShareGroupButtonDisable(groupState)}>
                        <Icon>
                          <Share />
                        </Icon>
                        Share group
                      </Menu.Item>
                    </>
                  ) : (
                    <></>
                  )}
                  <Menu.Item onSelect={openDeleteModalGroup}>
                    <Icon>
                      <Delete />
                    </Icon>
                    Delete group
                  </Menu.Item>
                </Menu.List>
              </Menu>
            ) : (
              <div className={styles.ProductTableCell__TableCell_Icons_Padding_Header} />
            )}
          </div>
        </div>
      );
    }

    return (
      <div className={styles.ProductTableCell__TableCell_Item}>
        <span className={styles.ProductTableCell__TableCell_Icons}>{getColorLegend()}</span>
        <div
          className={classnames([
            styles.TableViewboard__TableCell__grouping,
            styles.TableViewboard__TableCell__grouping__pgsWithDeleteButton,
          ])}
        >
          <GenericCell
            alignment="left"
            data-testid="generic-cell-item-name"
            primary={
              <>
                <span className={styles.GenericCell__Title}>{leaderboardKpi.item.itemBrand}</span>
                <span>{leaderboardKpi.item.itemName}</span>
              </>
            }
            className={styles.GenericCell}
          />
        </div>

        {selectedDisplayType === DisplayOptions.data && (
          <div className={styles.ProductTableCell__TableCell_Icons}>
            <ProductsFeatures feature={itemFeature.features} firstActivity={leaderboardKpi.item.firstActivityDate} />
          </div>
        )}

        {groupState?.isGroupingModelItem && isRowHovered && (
          <feedback.Tooltip placement="top">
            <feedback.Tooltip.Content>
              <p>Remove item model from group</p>
            </feedback.Tooltip.Content>
            <button
              type="button"
              className={styles.ProductTableCell__TableCell_Icons_Padding}
              aria-label={`Remove ${leaderboardKpi.item.itemBrand} ${leaderboardKpi.item.itemName} from group`}
              onClick={() =>
                groupState?.onDeleteGroupMgs?.(
                  leaderboardKpi.item.itemId,
                  `${leaderboardKpi.item.itemBrand} ${leaderboardKpi.item.itemName}`,
                )
              }
              onKeyDown={(event) => event.stopPropagation()}
            >
              <elements.Icon color="brand">
                <elements.Icon.icons.RemoveOutline />
              </elements.Icon>
            </button>
          </feedback.Tooltip>
        )}
      </div>
    );
  };

  if (isGroupingCell) {
    return (
      <td
        key={leaderboardKpi.item.itemName}
        className={classnames([
          styles.ProductTableCell__TableCell,
          styles.ProductTableCell__TableCell_Total,
          { [styles['ProductTableCell__StickyRow']]: isStickyRow },
        ])}
      >
        {returnGroupingCell(!!groupState.isGroupingHeader)}
      </td>
    );
  }

  if (isItemModel) {
    return (
      <td
        key={leaderboardKpi.item.itemName}
        className={classnames([
          styles.ProductTableCell__TableCell,
          styles.ProductTableCell__TableCell_Total,
          { [styles['ProductTableCell__StickyRow']]: isStickyRow },
        ])}
      >
        <div className={styles.ProductTableCell__TableCell_Item}>
          <span className={styles.ProductTableCell__TableCell_Icons}>{getColorLegend()}</span>
          <GenericCell
            alignment="left"
            data-testid="generic-cell-item-name"
            primary={
              <>
                <span className={styles.GenericCell__Title}>{leaderboardKpi.item.itemBrand}</span>
                <span>{leaderboardKpi.item.itemName}</span>
              </>
            }
            className={styles.GenericCell}
          />
          {selectedDisplayType === DisplayOptions.data && (
            <div className={styles.ProductTableCell__TableCell_Icons}>
              <ProductsFeatures feature={itemFeature.features} firstActivity={leaderboardKpi.item.firstActivityDate} />
            </div>
          )}
        </div>
      </td>
    );
  }

  if (ColumnHeader.firstActivity === headerKey) {
    return (
      <ProductsTableFirstActivityCell firstActivityDate={leaderboardKpi.item.firstActivityDate} cellAlignment="left" />
    );
  }

  if (ColumnHeader.marketShare === headerKey && selectedSaleType) {
    return (
      <td className={classnames([styles.ProductTableCell__TableCell, styles.ProductTableCell__TableCell_Total])}>
        <MarketShareCell
          backgroundColor={styles[getColorByIndex(rowIndex)]}
          ratio={leaderboardKpi.marketShareRatio}
          value={leaderboardKpi.marketShareValue}
        />
      </td>
    );
  }

  const renderFeatureCell = (isFeatureEmpty: boolean, feature?: Feature) => {
    if (groupState?.isGroupingHeader) {
      return <GenericCell alignment="left" primary={''} />;
    }
    return isFeatureEmpty ? (
      <GenericCell alignment="left" primary={FEATURE_UNAVAILABLE} />
    ) : (
      <GenericCell alignment="left" primary={feature?.value ?? ''} />
    );
  };
  if (headerKey.includes('feature')) {
    const featureIndex = headerKey.split('_')[1];
    const featureName = headerKey.split('_').splice(2).join();

    const feature = itemFeature.features.find((f) => f.name === featureName);
    const isFeatureEmpty = Number(featureIndex) === 0 && (!itemFeature.features || itemFeature.features.length === 0);

    return (
      <td className={classnames([styles.ProductTableCell__TableCell, styles.ProductTableCell__TableCell_Feature])}>
        {renderFeatureCell(isFeatureEmpty, feature)}
      </td>
    );
  }

  const renderDataCell = () => {
    return groupState?.isGroupEmpty ? (
      <GenericCell primary={'-'} />
    ) : (
      <ProductsTableDataCell
        headerKey={headerKey}
        cellContent={leaderboardKpi}
        currency={currency}
        selectedGrowthType={selectedGrowthType}
        selectedDisplayType={selectedDisplayType}
        isHideSecondaryCell={isPeriodYTDOrMAT(period)}
      />
    );
  };

  return (
    <td
      className={classnames([
        styles.ProductTableCell__TableCell,
        styles.ProductTableCell__TableCell_Kpi,
        sortedColumn && {
          [styles['ProductTableCell__SortedCell']]: sortedColumn === headerKey,
        },
      ])}
    >
      {isMarketRank(headerKey) || isRetailerRank(headerKey) ? (
        <ProductsTableRankCell ranking={displayRanking(headerKey)} />
      ) : (
        renderDataCell()
      )}
    </td>
  );
};

export default ProductsTableCell;
